import { TextArea as BlueprintJsTextArea } from "@blueprintjs/core";
import { css } from "@emotion/react";
import { FC, useRef } from "react";
import { v4 as uuid } from "uuid";
import { useFocusOnMount } from "../../hooks/useFocusOnMount";
import ReadonlyFormField from "../FormField/Readonly";
import FormField from "../FormField";
import { styles } from "./styles";
import { TextAreaProps } from "./types";

/** `TextArea` is a textarea input */
const TextArea: FC<TextAreaProps> = ({
  autoResize = false,
  disabled = false,
  error,
  offsetError,
  focusOnMount = false,
  fill,
  id = `kit-${uuid()}`,
  label,
  labelSuffix,
  labelSuffixAlign,
  hideLabel,
  description,
  minWidth = "none",
  name,
  onChange,
  onKeyDown,
  onKeyUp,
  onFocus,
  onBlur,
  placeholder = undefined,
  readonly = false,
  readonlyParser = undefined,
  required = false,
  value
}: TextAreaProps) => {
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  useFocusOnMount(inputRef, focusOnMount);

  if (readonly) {
    return (
      <ReadonlyFormField
        parser={readonlyParser}
        fill={fill}
        label={label}
        value={value}
        minWidth={minWidth}
        labelSuffix={labelSuffix}
        labelSuffixAlign={labelSuffixAlign}
        description={description}
        hideLabel={hideLabel}
      />
    );
  }

  return (
    <div
      className="kit-TextArea"
      css={[
        css`
          width: ${fill ? "100%" : "auto"};
          flex-grow: ${fill ? 1 : 0};
          margin: 0;
          display: flex;
          min-width: ${minWidth};
        `,
        styles.inputBase,
        !!disabled && styles.disabled,
        !!error && styles.withError
      ]}
    >
      <FormField
        fill={fill}
        label={label}
        error={error}
        offsetError={offsetError}
        required={required}
        labelSuffix={labelSuffix}
        labelSuffixAlign={labelSuffixAlign}
        description={description}
        hideLabel={hideLabel}
      >
        <BlueprintJsTextArea
          inputRef={inputRef}
          value={value}
          disabled={disabled}
          id={id}
          name={name}
          placeholder={placeholder}
          onChange={e => onChange(e.currentTarget.value)}
          growVertically={autoResize}
          required={required}
          onKeyDown={onKeyDown}
          onKeyUp={onKeyUp}
          onFocus={onFocus}
          onBlur={onBlur}
          fill={fill}
        />
      </FormField>
    </div>
  );
};

export default TextArea;
export * from "./types";
